import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Eye, Pencil, Trash2 } from "lucide-react";
import AdminLayout from "../../commponents/layout/adminLayout";
import { editProject, getProjectById } from "../../api/projectApi";
import Modal from "../../commponents/Modal";
import { showError, showSuccess } from "../../utils/ alert";
import { requiredText, runFieldValidation, type FieldErrors } from "../../helpers/validation";

interface Project {
  id: number;
  project_name: string;
  description: string;
  image: string;
  status: string;
  createdAt: string;
  updatedAt: string;
}

export default function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [project, setProject] = useState<Project | null>(null);
  const [openModal, setOpenModal] = useState(false);
 
  
    const [isEdit, setIsEdit] = useState(false);
    const [selectedId, setSelectedId] = useState<number | null>(null);
    const [projectName, setProjectName] = useState("");
    const [description, setDescription] = useState("");
    const [image, setImage] = useState<File | null>(null);
    const [previewImage, setPreviewImage] = useState("");
  const [projectImage, setProjectImage] = useState<File | null>(null);
  const [errors, setErrors] = useState<FieldErrors>({});

  useEffect(() => {
    fetchProjectDetail();
  }, []);

  const fetchProjectDetail = async () => {
    try {
      setLoading(true);

     const { data } = await getProjectById(Number(id));

      if (data) {
        setProject(data.data);
      }
    } catch (error) {
      showError(
        "Failed",
        "Failed to Load data."
    );
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="rounded-3xl bg-white p-8 shadow-sm">Loading...</div>
      </AdminLayout>
    );
  }

  if (!project) {
    return (
      <AdminLayout>
        <div className="rounded-3xl bg-white p-8 shadow-sm">
          Project tidak ditemukan.
        </div>
      </AdminLayout>
    );
  }

   const handleEdit = async (id: number) => {
     try {
       const { data } = await getProjectById(id);

       setProjectName(data.data.project_name);
       setDescription(data.data.description);
       setProjectImage(data.data.image);
       setImage(data.data.image);

       setPreviewImage(data.data.image);
       setSelectedId(id);
       setIsEdit(true);
       setOpenModal(true);
     } catch (error) {
       showError(
        "Failed",
        "Failed to Load data."
    );
     }
   };

  const handleSave = async () => {
      try {
       const fieldErrors = runFieldValidation(
         {
           project_name: projectName,
           description,
         },
         {
           project_name: requiredText("Project name is required"),
           description: requiredText("Description is required"),
         }
       );

       setErrors(fieldErrors);

       if (Object.keys(fieldErrors).length > 0) {
         return;
       }
  
        const formData = new FormData();
  
        formData.append("project_name", projectName);
        formData.append("description", description);
        formData.append("status", "Active");
  
        if (image) {
          formData.append("image", image);
        }
  
  
        if (isEdit && selectedId !== null) {
          await editProject(selectedId, formData);
  
          await showSuccess("Project updated successfully.");
        } 
  
        fetchProjectDetail();
  
        handleCloseModal();
      } catch (error) {
        showError(
        "Failed",
        "Failed to Load data."
    );
        showError("Failed to save project.");
      }
  };
  
  const handleUploadImage = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];

    if (!file) return;

    setImage(file);
    setPreviewImage(URL.createObjectURL(file));
  };


  const handleCloseModal = () => {
    setOpenModal(false);
    setProjectName("");
    setSelectedId(null);
    setIsEdit(false);
    setImage(null);
    setPreviewImage("");
    setDescription("");
  };

  const handleRemoveImage = () => {
    setImage(null);
    setPreviewImage("");
  };
  return (
    <AdminLayout>
      <div className="rounded-3xl bg-white p-8 shadow-sm">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <h1 className="text-2xl font-bold">Recent Project Detail</h1>

          <button
            onClick={() => handleEdit(project.id)}
            className="flex items-center gap-2 rounded-lg bg-blue-500 px-4 py-2 text-white hover:bg-blue-600">
            <Pencil size={16} />
            Edit
          </button>
        </div>

        {/* Image */}
        <div className="mb-8 overflow-hidden rounded-xl border border-gray-200">
          <img
            src={project.image}
            alt={project.project_name}
            className="h-[420px] w-full object-cover"
          />
        </div>

        {/* Project Name */}
        <div className="mb-8 grid grid-cols-[170px_20px_1fr]">
          <div className="font-semibold">Project Name</div>

          <div>:</div>

          <div>{project.project_name}</div>
        </div>

        {/* Description */}
        <div>
          <h2 className="mb-3 font-semibold">Description</h2>

          <div className="whitespace-pre-line rounded-xl border border-gray-300 p-5 leading-8">
            {project.description}
          </div>
        </div>
        <Modal
          open={openModal}
          title={isEdit ? "Edit Project" : "Add Project"}
          onClose={handleCloseModal}
          size="xl">
          <div className="w-[920px] max-w-full p-2">
            <div className="grid grid-cols-2 gap-10">
              {/* LEFT */}
              <div className="space-y-6">
                <div>
                  <label className="mb-2 block text-base font-semibold">
                    Project Name
                  </label>

                  <input
                    type="text"
                    value={projectName}
                    onChange={(e) => {
                      setProjectName(e.target.value);

                      if (errors.project_name) {
                        setErrors((prev) => {
                          const next = { ...prev };
                          delete next.project_name;
                          return next;
                        });
                      }
                    }}
                    placeholder="Type here"
                    className="h-12 w-full rounded-xl border border-gray-300 px-4 outline-none focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-base font-semibold">
                    Description
                  </label>

                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Type here"
                    rows={12}
                    className="h-[340px] w-full resize-none rounded-xl border border-gray-300 p-4 outline-none focus:border-blue-500"
                  />
                </div>
              </div>

              {/* RIGHT */}
              <div className="flex flex-col items-center">
                <div className="relative">
                  <div className="flex h-[340px] w-[340px] items-center justify-center overflow-hidden rounded-3xl bg-gray-100 border">
                    {previewImage ? (
                      <img
                        src={previewImage}
                        alt="preview"
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <div className="text-center text-gray-400">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="mx-auto mb-3 h-16 w-16"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={1.5}
                            d="M4 16l4-4 4 4 4-6 4 6"
                          />
                        </svg>

                        <p>No Image</p>
                      </div>
                    )}
                  </div>

                  {previewImage && (
                    <button
                      onClick={handleRemoveImage}
                      className="absolute -right-3 -top-3 flex h-10 w-10 items-center justify-center rounded-full bg-red-600 text-white shadow-lg hover:bg-red-700">
                      ✕
                    </button>
                  )}
                </div>

                <label
                  className="
                    mt-8
                    flex
                    h-14
                    w-[340px]
                    cursor-pointer
                    items-center
                    justify-center
                    rounded-xl
                    border-2
                    border-dashed
                    border-gray-300
                    text-green-600
                    transition
                    hover:bg-green-50
                  ">
                  Upload Image
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={handleUploadImage}
                  />
                </label>
              </div>
            </div>

            {/* FOOTER */}

            <div className="mt-10 flex justify-end gap-4">
              <button
                onClick={handleCloseModal}
                className="rounded-xl bg-red-600 px-7 py-3 font-medium text-white hover:bg-red-700">
                Cancel
              </button>

              <button
                onClick={handleSave}
                className="rounded-xl bg-green-600 px-7 py-3 font-medium text-white hover:bg-green-700">
                {isEdit ? "Update" : "Save"}
              </button>
            </div>
          </div>
        </Modal>
      </div>
    </AdminLayout>
  );
}
function setErrors(fieldErrors: FieldErrors) {
  throw new Error("Function not implemented.");
}

